import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { MenuOutboundLayoutContext } from 'widgets/Menu/ui/MenuOutbound/context/menuOutboundLayoutContext'
import { MenuOutboundLayoutStore } from 'widgets/Menu/ui/MenuOutbound/store/menuOutboundLayoutStore'
import { IntegrationDetailsStore } from 'pages/settings/pages/integrations/store/integrationDetailsStore'
import { IntegrationWebhooksStore } from 'pages/settings/pages/integrations/pages/Webhooks/store/IntegrationWebhooksStore'
import { useEventIntegrationConnectedSettingsDetails } from 'pages/settings/pages/integrations/events/EventIntegrationConnectedSettings'

const WebhookIntegrationContext = createContext<{
  integrationStore: IntegrationDetailsStore
  webhooksStore: IntegrationWebhooksStore
} | null>(null)

export const useWebhookIntegrationContext = () => {
  const context = useContext(WebhookIntegrationContext)

  if (context === null)
    throw new Error(
      'You have forgotten to wrap your root component with WebhookIntegrationProvider'
    )

  return context
}

const WebhookIntegrationProvider = ({ children }: { children: React.ReactNode }) => {
  const entities = useEntitiesContext()
  const integrationStore = useMemo(() => new IntegrationDetailsStore('webhook', entities), [])
  const webhooksStore = useMemo(() => new IntegrationWebhooksStore(entities), [])

  useEventIntegrationConnectedSettingsDetails(integrationStore)

  useEffect(() => {
    void integrationStore.getIntegrationDetails()
    void webhooksStore.init()
    return () => {
      integrationStore.dispose()
      webhooksStore.dispose()
    }
  }, [])

  return (
    <WebhookIntegrationContext.Provider value={{ integrationStore, webhooksStore }}>
      {children}
    </WebhookIntegrationContext.Provider>
  )
}

export const WebhookLayout = () => {
  const [menuLayoutStore] = useState(() => new MenuOutboundLayoutStore())

  return (
    <MenuOutboundLayoutContext.Provider value={menuLayoutStore}>
      <WebhookIntegrationProvider>
        <Outlet />
      </WebhookIntegrationProvider>
    </MenuOutboundLayoutContext.Provider>
  )
}
